import { createSlice } from '@reduxjs/toolkit';
import { capitalizeString } from '@strata/helper-functions';

import { getReferralDetails } from 'api/clientAPI';
import { getReferralHistory } from 'api/prescriberAPI';
import { handleError } from 'services/Error';

export const referralHistorySlice = createSlice({
    name: 'referralHistory',
    initialState: {
        referralsData: [],
        referralsDataLoaded: false,
        referralDetails: {},
    },
    reducers: {
        setReferralsData(state, action) {
            return { ...state, referralsData: action.payload, referralsDataLoaded: true };
        },
        updateReferralDetails(state, action) {
            return {
                ...state,
                referralDetails: {
                    ...state.referralDetails,
                    [action.payload.referralId]: action.payload,
                },
            };
        },
    },
});

export const { setReferralsData, updateReferralDetails } = referralHistorySlice.actions;

export const fetchReferralHistory = () => async (dispatch) => {
    try {
        const response = await getReferralHistory();
        const referralsData = response.map((referral) => ({
            referralId: referral.referral_id,
            payerId: referral.payer_id,
            segmentId: referral.segment_id,
            patientName: capitalizeString(referral.patient_name),
            memberAge: referral.member_age,
            memberCity: referral.member_city,
            memberState: referral.member_state,
            memberZip: referral.member_zip,
            prescriberId: referral.prescriber_id,
            prescriberName: capitalizeString(referral.prescriber_name),
            supplierNpi: referral.supplier_npi,
            supplierName: referral.supplier_name,
            supplierPhone: referral.supplier_phone,
            productServiceOid: referral.product_service_oid,
            medication: referral.drug_name,
            diagnosisCode: referral.diagnosis_code,
            productQty: referral.product_qty,
            incentiveTotal: referral.incentive_total,
            referralStatus: referral.referral_status?.trim(),
            submittedAt: referral.submitted_at,
        }));
        dispatch(setReferralsData(referralsData));
    } catch (err) {
        handleError('An error occurred retrieving referral history.', err);
        dispatch(setReferralsData([]));
    }
};

export const fetchReferralDetails = (referralId, payerId, prescriberId) => async (dispatch) => {
    try {
        const response = await getReferralDetails(referralId, payerId, prescriberId);
        const referralDetails = {
            referralId: response.referral_id,
            caseId: response.case_id,
            memberId: response.member_id,
            memberFirstName: response.firstname,
            memberLastName: response.lastname,
            memberAddress1: response.member_address1,
            memberAddress2: response.member_address2,
            memberCity: response.member_city,
            memberState: response.member_state,
            memberZip: response.member_zip,
            memberPhone: response.phone,
            memberEmail: response.email,
            memberDOB: response.date_of_birth,
            memberGender: response.gender,
            healthPlan: response.health_plan,
            payerId: response.payer_id,
            segmentId: response.segment_id,
            segmentName: response.segment_name,
            supplierNpi: response.supplier_npi,
            productServiceOid: response.product_service_oid,
            medication: response.product_service_display_name,
            strength: response.strength,
            quantity: response.product_qty,
            refills: response.rx_refillcount,
            directions: response.rx_sig,
            specialInstructions: response.special_instructions,
            referralStatus: response.referral_status?.trim(),
            practiceNpi: response.practice_npi,
            prescriberNpi: response.prescriber_npi,
            prescriberName: capitalizeString(response.prescriber_name),
            placeOfService: response.place_of_service?.trim(),
            referralExpiration: response.referral_expiration,
            diagnosis: response.dx_display_name,
            diagnosisOid: response.dx_oid,
            diagnosisCode: response.diagnosis_code,
            clinicalInfo: JSON.parse(response.clinical_info ?? '{}'),
            submittedBy: response.submitted_by,
            submittedAt: response.submitted_at,
            friendlyId: response.friendly_id,
            benefit: response.benefit?.trim(),
        };
        dispatch(updateReferralDetails(referralDetails));
    } catch (err) {
        handleError('An error occurred retrieving referral details.', err);
        dispatch(updateReferralDetails({ referralId }));
    }
};
